import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail, homePath } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, Home, Briefcase } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "cleaner" ? "cleaner" : "pm");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", service_area: "", bio: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await register({ ...form, role });
      toast.success("Account created!");
      navigate(homePath(u.role));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-sapphire text-white p-12">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 bg-ocean flex items-center justify-center rounded-md"><Sparkles className="w-5 h-5" /></div>
          <span className="font-heading font-extrabold text-xl">Clean Slate Connect</span>
        </Link>
        <div>
          <h1 className="font-heading font-black text-4xl tracking-tight leading-tight">Join the network.</h1>
          <p className="text-white/60 mt-4 max-w-md">Whether you manage properties or clean them, get set up in minutes.</p>
        </div>
        <span className="text-white/40 text-sm">© 2026 Clean Slate Connect. Owned and operated by Clean Slate Biohazard Response Team LLC.</span>
      </div>

      <div className="flex items-center justify-center p-6 bg-background overflow-y-auto">
        <form onSubmit={submit} className="w-full max-w-sm py-8" data-testid="register-form">
          <h2 className="font-heading font-extrabold text-3xl tracking-tight text-sapphire mb-1">Create account</h2>
          <p className="text-muted-foreground text-sm mb-6">Choose how you'll use Clean Slate Connect.</p>

          <div className="grid grid-cols-2 gap-3 mb-6">
            {[
              { v: "pm", label: "I'm a Host", icon: Home },
              { v: "cleaner", label: "I'm a Cleaner", icon: Briefcase },
            ].map((r) => (
              <button
                type="button"
                key={r.v}
                onClick={() => setRole(r.v)}
                data-testid={`role-${r.v}`}
                className={`border rounded-md p-3 flex flex-col items-center gap-1.5 transition-colors ${
                  role === r.v ? "border-ocean bg-ocean/5 text-ocean" : "border-border text-muted-foreground hover:border-slate-300"
                }`}
              >
                <r.icon className="w-5 h-5" />
                <span className="text-sm font-semibold">{r.label}</span>
              </button>
            ))}
          </div>

          {error && <div className="mb-4 text-sm bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded" data-testid="register-error">{error}</div>}

          <div className="space-y-3">
            <div>
              <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Full Name</Label>
              <Input required value={form.name} onChange={set("name")} data-testid="register-name" />
            </div>
            <div>
              <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Email</Label>
              <Input type="email" required value={form.email} onChange={set("email")} data-testid="register-email" />
            </div>
            <div>
              <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Password</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password" />
            </div>
            <div>
              <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Phone</Label>
              <Input value={form.phone} onChange={set("phone")} data-testid="register-phone" />
            </div>
            {role === "cleaner" && (
              <div>
                <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Service Area</Label>
                <Input value={form.service_area} onChange={set("service_area")} placeholder="e.g. Austin, TX" data-testid="register-service-area" />
              </div>
            )}
          </div>

          <Button type="submit" disabled={loading} className="w-full mt-6 bg-ocean hover:bg-ocean/90" data-testid="register-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Account"}
          </Button>

          <p className="text-sm text-center mt-6 text-muted-foreground">
            Already have an account? <Link to="/login" className="text-ocean font-semibold" data-testid="link-login">Log in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
